import { forwardRef, type ImgHTMLAttributes } from "react";
import { imgProps, imgSrcSetAvif, imgSizes, lcpImgProps } from "@/lib/images";

type Props = {
  name: string;
  sizes?: string;
  alt: string;
} & Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet" | "sizes" | "width" | "height" | "alt">;

/** Responsive <picture> with AVIF source + WebP img fallback.
 *  The LCP photo skips <picture>: React 19 only hoists a fetchpriority=high
 *  preload from a bare <img>, and that URL must match the head preload. */
export const ResponsiveImg = forwardRef<HTMLImageElement, Props>(function ResponsiveImg(
  { name, sizes = imgSizes.hero, alt, ...rest },
  ref,
) {
  const priority = rest.fetchPriority === "high";
  const webp = imgProps(name, sizes);
  const avif = imgSrcSetAvif(name);

  if (priority) {
    return <img ref={ref} alt={alt} loading="eager" {...lcpImgProps(name, sizes)} {...rest} />;
  }

  const image = <img ref={ref} alt={alt} {...webp} {...rest} />;
  if (!avif) return image;

  return (
    <picture className="contents">
      <source type="image/avif" srcSet={avif} sizes={webp.sizes ?? sizes} />
      {image}
    </picture>
  );
});
